import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';

const LicenseEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    company_name: '',
    license_key: '',
    tier_id: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    start_date: '',
    expiry_date: '',
    max_users: 1,
    status: 'active',
    notes: '',
  });
  const [tiers, setTiers] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    try {
      const response = await api.getLicenseTiers();
      if (response.success) {
        setTiers(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load tiers:', error);
    }
  };

  useEffect(() => {
    loadLicense();
  }, [id]);

  const loadLicense = async () => {
    try {
      setPageLoading(true);
      const response = await api.getLicenseById(id);
      if (response.success) {
        const license = response.data;
        setFormData({
          company_name: license.company_name || '',
          license_key: license.license_key || '',
          tier_id: license.tier_id || '',
          contact_name: license.contact_name || '',
          contact_email: license.contact_email || '',
          contact_phone: license.contact_phone || '',
          start_date: license.start_date ? license.start_date.split('T')[0] : '',
          expiry_date: license.expiry_date ? license.expiry_date.split('T')[0] : '',
          max_users: license.max_users || 1,
          status: license.status || 'active',
          notes: license.notes || '',
        });
      } else {
        setError('License not found');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setPageLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.updateLicense(id, formData);
      if (response.success) {
        navigate('/licenses');
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setError('')}>&times;</button>
          {error}
        </div>
      )}

      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Edit License #{id}</h3>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="card-body">
                <div className="form-group">
                  <label>License Tier</label>
                  <select
                    className="form-control"
                    name="tier_id"
                    value={formData.tier_id}
                    onChange={handleChange}
                  >
                    <option value="">No tier</option>
                    {tiers.map(tier => (
                      <option key={tier.id} value={tier.id}>
                        {tier.name} - {tier.currency} {tier.price_monthly}/month
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Company Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>License Key</label>
                  <input
                    type="text"
                    className="form-control"
                    name="license_key"
                    value={formData.license_key}
                    readOnly
                    style={{ backgroundColor: '#e9ecef' }}
                  />
                  <small className="form-text text-muted">License key cannot be changed</small>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Contact Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="contact_name"
                        value={formData.contact_name}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Contact Email</label>
                      <input
                        type="email"
                        className="form-control"
                        name="contact_email"
                        value={formData.contact_email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    type="text"
                    className="form-control"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Start Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Expiry Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        name="expiry_date"
                        value={formData.expiry_date}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Max Users *</label>
                      <input
                        type="number"
                        className="form-control"
                        name="max_users"
                        value={formData.max_users}
                        onChange={handleChange}
                        min="1"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        className="form-control"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                      >
                        <option value="active">Active</option>
                        <option value="expiring">Expiring</option>
                        <option value="expired">Expired</option>
                        <option value="suspended">Suspended</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    className="form-control"
                    name="notes"
                    rows="3"
                    value={formData.notes}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="card-footer">
                <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                  <i className="fas fa-save"></i> {loading ? 'Updating...' : 'Update License'}
                </button>
                <button type="button" className="btn btn-default btn-sm ml-2" onClick={() => navigate('/licenses')}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default LicenseEdit;


